import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from "react";
import Trash2 from "@crehana/compass.icons.outline/Trash2";
import { useTranslation } from "@crehana/i18n";
import { useDebounce } from "@crehana/react-hooks";
import { notEmpty } from "@crehana/utils";
import { InputField } from "../../InputField/index.js";
import { Button } from "../Button/index.js";
import { ChipGroup } from "../ChipGroup/index.js";
import { ConditionalSelect, DefaultConditionalTypesEnum, defaultOptions } from "../conditional-select/index.js";
import { DatePickerWithTabs } from "../DatePicker/date-picker-with-tabs.js";
import { AsyncCheckboxesDropdown, Dropdown, DropdownContent, DropdownValue } from "../Dropdown/index.js";
import { AsyncLeveledDropdown } from "../Dropdown/pre-built-variants/async/async-leveled-dropdown.js";
import { useDefaultValues } from "./hooks/use-default-values.js";
import { useSegmentationState } from "./hooks/use-segmentation-state.js";
import i18n from "./i18n/index.js";
import { TCustomFieldTypeEnum } from "./types.js";
import { mapStateToSegmentationCondition } from "./utils.js";
const LabelWrapper = ({ label, children })=>/*#__PURE__*/ jsxs("div", {
        className: "tw-flex tw-flex-col tw-w-full",
        children: [
            /*#__PURE__*/ jsx("label", {
                className: "tw-font-subtitle5 tw-mb-0.8 tw-block",
                children: label
            }),
            children
        ]
    });
const SegmentationFieldInput = ({ categories, defaultCategory = 'alphabetic', defaultValue, onCategorySelect, onFieldSelect, onChange, onDelete })=>{
    const { languageCode } = useTranslation();
    const t = i18n[languageCode];
    const { debounceValue, handleChange } = useDebounce();
    const { defaultCategory: initialCategory, defaultFieldId, defaultFieldLabel, defaultSelectedValues, defaultInputValue, defaultDateValue, defaultConditionalType, defaultFormType } = useDefaultValues(defaultValue);
    const { state, updateState, updateValue, handleClear } = useSegmentationState({
        category: initialCategory || defaultCategory,
        fieldId: defaultFieldId,
        availableFields: [],
        fieldValues: [],
        conditionalType: defaultConditionalType,
        isTagSelect: false,
        isLoadingValues: false,
        value: {
            selected: defaultSelectedValues,
            input: defaultInputValue,
            date: defaultDateValue
        }
    });
    const fieldLabel = state.availableFields.find((f)=>f.value == state.fieldId)?.label || defaultFieldLabel || state.fieldId;
    const handleCategorySelect = async (category)=>{
        handleClear();
        const availableFields = await onCategorySelect?.(category);
        updateState({
            category: category,
            availableFields: availableFields || []
        });
        return (availableFields || []).map((f)=>({
                ...f,
                type: 'text'
            }));
    };
    const handleAttributeSelect = async (values)=>{
        const [[category, value]] = Object.entries(values);
        const attributeId = value[0];
        updateState({
            category: category,
            fieldId: attributeId
        });
        if (onFieldSelect && 'alphabetic' === category) handleFetchFieldValues(attributeId);
    };
    const handleFetchFieldValues = async (attributeId, searchTerm)=>{
        if (!onFieldSelect) return;
        updateState({
            isLoadingValues: true
        });
        try {
            const result = onFieldSelect(attributeId, searchTerm);
            const values = result instanceof Promise ? await result : result;
            updateState({
                fieldValues: values,
                isLoadingValues: false
            });
        } catch (error) {
            updateState({
                fieldValues: [],
                isLoadingValues: false
            });
        }
    };
    const handleConditionalSelect = (value, inputValues)=>{
        const selectedOption = defaultOptions.find((opt)=>opt.value === value);
        if (!selectedOption) return;
        const isSelectType = 'alphabetic' === state.category && Boolean(selectedOption.select);
        const isNumericRange = 'numeric' === state.category && value === DefaultConditionalTypesEnum.RANGE;
        updateState({
            isTagSelect: isSelectType,
            conditionalType: selectedOption.value.toString()
        });
        if (inputValues?.length) if (isNumericRange) updateValue({
            input: inputValues
        });
        else isSelectType ? updateValue({
            selected: inputValues,
            input: void 0
        }) : updateValue({
            input: inputValues[0],
            selected: void 0
        });
    };
    const handleChipDelete = (value)=>{
        if ('alphabetic' !== state.category || !state.isTagSelect) return;
        updateValue({
            selected: state.value?.selected?.filter((v)=>v !== value)
        });
    };
    useEffect(()=>{
        if (void 0 === debounceValue || '' === debounceValue) return;
        if (Array.isArray(state.value?.input) && 2 === state.value.input.length) return;
        updateValue({
            input: debounceValue
        });
    }, [
        debounceValue
    ]);
    const isInitialized = useRef(false);
    useEffect(()=>{
        if (isInitialized.current) return;
        if (defaultFormType === TCustomFieldTypeEnum.Number && defaultValue?.values?.[0]?.operator === DefaultConditionalTypesEnum.RANGE && defaultValue?.values?.[0]?.value?.length === 2) {
            updateValue({
                input: defaultValue.values[0].value
            });
            updateState({
                conditionalType: DefaultConditionalTypesEnum.RANGE
            });
        }
        isInitialized.current = true;
    }, []);
    useEffect(()=>{
        const parsedData = mapStateToSegmentationCondition(state, fieldLabel);
        if (parsedData.isValid) onChange?.(parsedData.data);
    }, [
        state.value?.input,
        state.value?.selected,
        state.value?.date?.tab,
        state.value?.date?.dates.from,
        state.value?.date?.dates.to,
        state.conditionalType
    ]);
    useEffect(()=>{
        if (defaultFormType && state.fieldId) {
            const isListType = defaultFormType === TCustomFieldTypeEnum.Multiple;
            updateState({
                isTagSelect: isListType
            });
            if (onFieldSelect && defaultFormType === TCustomFieldTypeEnum.Multiple) handleAttributeSelect({
                [state.category]: [
                    state.fieldId
                ]
            });
        }
    }, []);
    useEffect(()=>{
        if (void 0 === debounceValue || !state.fieldId) return;
        if (onFieldSelect) handleFetchFieldValues(state.fieldId, debounceValue);
    }, [
        debounceValue,
        state.fieldId
    ]);
    const renderInputByCategory = ()=>{
        if (!state.category) return null;
        switch(state.category){
            case 'alphabetic':
                return /*#__PURE__*/ jsxs(Fragment, {
                    children: [
                        /*#__PURE__*/ jsx(LabelWrapper, {
                            label: t.CONDITIONAL,
                            children: /*#__PURE__*/ jsx(ConditionalSelect, {
                                placeholder: t.CONDITIONAL_PLACEHOLDER,
                                onSelect: ({ option, values })=>handleConditionalSelect(option, values),
                                defaultValue: {
                                    option: state.conditionalType
                                },
                                disabled: !state.fieldId,
                                showValueInDropdown: true,
                                noInputs: true
                            })
                        }),
                        state.isTagSelect ? /*#__PURE__*/ jsx(LabelWrapper, {
                            label: t.VALUE,
                            children: /*#__PURE__*/ jsx(AsyncCheckboxesDropdown, {
                                debounceTime: 0,
                                onSearch: (searchTerm)=>handleChange({
                                        target: {
                                            value: searchTerm
                                        }
                                    }),
                                selected: state.value?.selected?.map((v)=>({
                                        value: v,
                                        label: v.toString()
                                    })),
                                options: state.fieldValues || [],
                                loading: state.isLoadingValues,
                                onSelect: (value)=>updateValue({
                                        selected: value?.map((v)=>v.value)
                                    }),
                                placeholder: t.VALUE_PLACEHOLDER,
                                searchProps: {
                                    searchPlaceholder: t.SEARCH_PLACEHOLDER,
                                    noFoundPlaceholderTitle: t.NO_RESULTS
                                }
                            })
                        }) : /*#__PURE__*/ jsx(InputField, {
                            placeholder: t.VALUE_PLACEHOLDER,
                            label: t.VALUE,
                            containerClassName: "tw-flex tw-flex-col tw-w-full",
                            onChange: handleChange,
                            disabled: !state.fieldId && !state.conditionalType,
                            defaultValue: state.value?.input
                        })
                    ]
                });
            case 'date':
                return /*#__PURE__*/ jsx(LabelWrapper, {
                    label: t.DATE,
                    children: /*#__PURE__*/ jsx(DatePickerWithTabs, {
                        className: "tw-flex tw-w-full tw-flex-1 tw-flex-grow-0",
                        enabledTabs: [
                            'single',
                            'range'
                        ],
                        defaultTab: state.value?.date?.tab,
                        value: state.value?.date?.dates,
                        onChange: (value)=>updateValue({
                                date: value
                            }),
                        disabled: !state.fieldId
                    })
                });
            case 'numeric':
                {
                    const defaultInputValueArray = (Array.isArray(defaultInputValue) ? defaultInputValue : [
                        defaultInputValue
                    ]).filter(notEmpty);
                    return /*#__PURE__*/ jsx(LabelWrapper, {
                        label: t.CONDITIONAL,
                        children: /*#__PURE__*/ jsx(ConditionalSelect, {
                            placeholder: t.CONDITIONAL_PLACEHOLDER,
                            onSelect: ({ option, values })=>handleConditionalSelect(option, values),
                            defaultValue: {
                                option: defaultConditionalType,
                                values: defaultInputValueArray
                            },
                            showValueInDropdown: true,
                            type: "number"
                        })
                    });
                }
            default:
                return null;
        }
    };
    return /*#__PURE__*/ jsx(Fragment, {
        children: /*#__PURE__*/ jsxs("div", {
            className: "tw-flex tw-flex-col tw-w-full tw-bg-neutral-light-100 tw-p-2.4 tw-rounded-[1.2rem] tw-mb-[1.4rem]",
            children: [
                /*#__PURE__*/ jsxs("div", {
                    className: "tw-flex tw-justify-between tw-items-center tw-gap-1",
                    children: [
                        /*#__PURE__*/ jsxs("div", {
                            className: "tw-flex tw-flex-col tw-w-full tw-max-w-20",
                            children: [
                                /*#__PURE__*/ jsx("label", {
                                    className: "tw-font-subtitle5 tw-mb-0.8 tw-block",
                                    children: t.ATTRIBUTES
                                }),
                                /*#__PURE__*/ jsxs(Dropdown, {
                                    children: [
                                        /*#__PURE__*/ jsx(DropdownValue, {
                                            className: "tw-truncate",
                                            placeholder: t.ATTRIBUTE_PLACEHOLDER,
                                            forcedValue: fieldLabel
                                        }),
                                        /*#__PURE__*/ jsx(DropdownContent, {
                                            children: /*#__PURE__*/ jsx(AsyncLeveledDropdown, {
                                                categories: categories,
                                                onCategorySelect: handleCategorySelect,
                                                onSelectionChange: (values)=>handleAttributeSelect(values),
                                                trackByCategory: true,
                                                multiselect: false
                                            })
                                        })
                                    ]
                                })
                            ]
                        }),
                        renderInputByCategory(),
                        /*#__PURE__*/ jsx(Button, {
                            type: "button",
                            variant: "icon",
                            className: "tw-mt-2.4",
                            onClick: onDelete,
                            children: /*#__PURE__*/ jsx(Trash2, {
                                size: 24,
                                className: "tw-text-neutral-light-500"
                            })
                        })
                    ]
                }),
                state.isTagSelect && state.value?.selected && state.value.selected.length > 0 && /*#__PURE__*/ jsx(ChipGroup, {
                    items: state.value.selected.map((v)=>({
                            value: v,
                            label: state.fieldValues.find((av)=>av.value === v)?.label || v.toString()
                        })),
                    selected: state.value.selected.map((v)=>v.toString()),
                    onDelete: (value)=>handleChipDelete(value),
                    onClearAll: ()=>{
                        updateValue({
                            selected: []
                        });
                        onChange?.(void 0);
                    },
                    maxChipsRows: 2,
                    clearButtonLabel: t.CLEAR_ALL
                })
            ]
        })
    });
};
export { SegmentationFieldInput };
